/**
 * @fileoverview Write-Ahead Queue (WAQ) for resilient, idempotent mutations.
 * - Enqueue mutations in IndexedDB (Dexie)
 * - Drain with retry/backoff + jitter
 * - Use idempotency keys for dedupe on server
 */

import logger from '../../utils/logger';
import { waqDB } from './db';
import { increment, set as setMetric } from '../metrics';

/**
 * Generate a reasonably unique identifier without relying on ESM-only deps.
 *
 * Jest (via CRA) doesn't transpile node_modules, so importing the ESM-only
 * `nanoid` package caused the test runner to bail before the suites even ran.
 * Keeping the ID generation in this module lets us avoid that incompatibility
 * while still producing collision-resistant keys for queue items.
 */
function generateId() {
  const cryptoObj = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID().replace(/-/g, '');
  }
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    const bytes = cryptoObj.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  const timePart = Date.now().toString(36);
  const randPart = Math.random().toString(36).slice(2, 10);
  return `${timePart}${randPart}`;
}

/**
 * Compute next attempt time using exponential backoff with jitter.
 * @param {number} attemptCount 0-based attempts
 */
function computeNextAttemptAt(attemptCount) {
  const base = 500; // ms
  const max = 60_000; // 60s cap
  const exp = Math.min(max, base * Math.pow(2, attemptCount));
  const jitter = Math.floor(Math.random() * 250);
  return Date.now() + exp + jitter;
}

/**
 * Enqueue a mutation job.
 * @param {{
 *  userId: string,
 *  type: string,
 *  entity: string,
 *  entityId?: string,
 *  payload: any,
 *  id?: string,
 *  idempotencyKey?: string,
 * }} job
 */
export async function enqueue(job) {
  const id = job.id || generateId();
  const idempotencyKey = job.idempotencyKey || generateId();
  const record = {
    id,
    userId: job.userId,
    type: job.type,
    entity: job.entity,
    entityId: job.entityId || null,
    payload: job.payload || {},
    idempotencyKey,
    status: 'pending',
    attemptCount: 0,
    createdAt: Date.now(),
    nextAttemptAt: Date.now(),
    lastError: null,
  };
  await waqDB.table('mutations').put(record);
  logger.info('WAQ: enqueued', { id, type: job.type, entity: job.entity, entityId: job.entityId });
  try {
    increment('write_queue_enqueued', 1);
  } catch (_) {
    // ignore metrics errors
  }
  return { id, idempotencyKey };
}

let draining = false;

// ---- Cross-tab mutex (best-effort via localStorage) ----
const LOCK_KEY = 'timegenie_waq_lock';
const TAB_ID =
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `tab_${Math.random().toString(36).slice(2)}`;

/**
 * Try to acquire a process-wide lock for draining. Returns true if acquired.
 * Lock has a short TTL; renewed on each acquire.
 */
function tryAcquireLock() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return true; // SSR/tests: allow
    const now = Date.now();
    const ttlMs = 5000; // 5s
    const raw = window.localStorage.getItem(LOCK_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        // If lock is stale, steal it
        if (!parsed || typeof parsed.expiresAt !== 'number' || parsed.expiresAt < now) {
          window.localStorage.setItem(
            LOCK_KEY,
            JSON.stringify({ tabId: TAB_ID, expiresAt: now + ttlMs }),
          );
          return true;
        }
        // If held by this tab, renew
        if (parsed.tabId === TAB_ID) {
          window.localStorage.setItem(
            LOCK_KEY,
            JSON.stringify({ tabId: TAB_ID, expiresAt: now + ttlMs }),
          );
          return true;
        }
        // Held by another tab and still valid
        return false;
      } catch (_) {
        // Corrupt; replace
        window.localStorage.setItem(
          LOCK_KEY,
          JSON.stringify({ tabId: TAB_ID, expiresAt: now + ttlMs }),
        );
        return true;
      }
    } else {
      window.localStorage.setItem(
        LOCK_KEY,
        JSON.stringify({ tabId: TAB_ID, expiresAt: now + ttlMs }),
      );
      return true;
    }
  } catch (e) {
    // If storage is unavailable, proceed without lock
    void e;
    return true;
  }
}

function releaseLock() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const raw = window.localStorage.getItem(LOCK_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.tabId === TAB_ID) {
      window.localStorage.removeItem(LOCK_KEY);
    }
  } catch (e) {
    // ignore
    void e;
  }
}

/**
 * Drain once: processes due pending/failed items.
 */
export async function drainOnce() {
  if (draining) return;
  // Acquire cross-tab lock
  const haveLock = tryAcquireLock();
  if (!haveLock) return; // another tab will drain shortly
  draining = true;
  try {
    const now = Date.now();
    const due = await waqDB.table('mutations').where('nextAttemptAt').belowOrEqual(now).toArray();

    try {
      setMetric('write_queue_depth', due.length || 0);
    } catch (e) {
      // ignore metrics errors
      void e;
    }

    // Process sequentially for simplicity; can parallelize by entity in future.
    for (const job of due) {
      try {
        await processJob(job);
        await waqDB.table('mutations').delete(job.id);
        logger.info('WAQ: job succeeded', { id: job.id });
        try {
          increment('write_queue_drained_success', 1);
        } catch (e) {
          void e;
        }
      } catch (e) {
        const attempt = (job.attemptCount || 0) + 1;
        const next = computeNextAttemptAt(attempt);
        /** @type {any} */
        const errAny = e;
        const errMsg = errAny && (errAny.message || String(errAny));
        await waqDB.table('mutations').update(job.id, {
          attemptCount: attempt,
          nextAttemptAt: next,
          status: attempt >= 8 ? 'dlq' : 'pending',
          lastError: String(errMsg),
        });
        logger.warn('WAQ: job failed, rescheduled', { id: job.id, attempt, next });
        try {
          increment('mutation_retry_count', 1);
        } catch (e) {
          void e;
        }
      }
    }
  } finally {
    draining = false;
    releaseLock();
  }
}

/**
 * Start a simple foreground drainer loop.
 */
export function startDrainer() {
  // Light polling loop; later we can tie into visibility/online events and SW
  setInterval(() => {
    drainOnce().catch(() => {});
  }, 3000);
}

/**
 * Injected processors per job type.
 */
const processors = new Map();

/**
 * Register a processor for a job type.
 * @param {string} type
 * @param {(job:any)=>Promise<void>} fn
 */
export function registerProcessor(type, fn) {
  processors.set(type, fn);
}

/**
 * Internal: process a job using its type-specific handler.
 */
/** @param {any} job */
async function processJob(job) {
  const fn = processors.get(job.type);
  if (!fn) throw new Error(`No processor for job type: ${job.type}`);
  await fn(job);
}

export default {
  enqueue,
  drainOnce,
  startDrainer,
  registerProcessor,
};
